<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content large">
      <div class="modal-header">
        <h2 class="modal-title">{{ $t('household.operationForm.titles.' + type) }}</h2>
        <button @click="$emit('close')" class="close-btn">
          <LucideIcon :size="24">
            <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
          </LucideIcon>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-form-scroll">
        <div class="form-body">
          <!-- Основная информация -->
          <div class="form-grid-3">
            <div class="form-group">
              <label>{{ $t('household.operationForm.dateLabel') }}</label>
              <DatePicker v-model="formData.date" format="iso" />
            </div>

            <div class="form-group">
              <label>{{ $t('household.operationForm.warehouseLabel') }}</label>
              <select v-model="formData.warehouseId" required>
                <option v-for="w in warehouses" :key="w.id" :value="w.id">
                  {{ w.name }}
                </option>
              </select>
            </div>

            <div v-if="['receipt', 'consumption'].includes(type)" class="form-group">
              <label>{{ type === 'receipt' ? $t('household.operationForm.supplierLabel') : $t('household.operationForm.customerLabel') }}</label>
              <select v-model="formData.counterpartyId">
                <option value="">{{ $t('household.operationForm.notSelected') }}</option>
                <option v-for="c in filteredCounterparties" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Товары -->
          <div class="items-section">
            <div class="section-header">
              <h3 class="section-title">{{ $t('household.operationForm.itemsSectionTitle') }}</h3>
              <button type="button" @click="handleAddItem" class="btn-primary small">
                <LucideIcon :size="14" class="mr-1">
                  <path d="M5 12h14"/><path d="M12 5v14"/>
                </LucideIcon>
                {{ $t('household.operationForm.addPositionBtn') }}
              </button>
            </div>

            <div class="items-list">
              <div v-for="(item, index) in items" :key="index" class="item-card">
                <div class="item-grid-top">
                  <div class="form-group full-width">
                    <label>{{ $t('household.operationForm.nomenclatureLabel') }}</label>
                    <select
                      v-model="item.nomenclatureId"
                      required
                      @change="onNomenclatureChange(index)"
                    >
                      <option v-for="n in nomenclatures" :key="n.id" :value="n.id">
                        {{ n.name }} ({{ n.unit }})
                      </option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label>{{ $t('household.operationForm.quantityLabel') }}</label>
                    <input
                      type="number"
                      v-model.number="item.quantity"
                      required
                      min="0"
                      step="0.01"
                      @input="onQuantityChange(index)"
                    />
                  </div>

                  <div class="form-group">
                    <label>{{ $t('household.operationForm.costLabel') }}</label>
                    <input type="number" v-model.number="item.cost" required min="0" />
                  </div>

                  <div class="form-group">
                    <label>{{ $t('household.operationForm.batchLabel') }}</label>
                    <input type="text" v-model="item.batchNumber" :placeholder="$t('household.operationForm.batchPlaceholder')" />
                  </div>
                </div>

                <!-- Адрес хранения -->
                <div class="location-group">
                  <label class="group-label">
                    {{ type === 'movement' ? $t('household.operationForm.fromLabel') : $t('household.operationForm.storageAddressLabel') }}
                  </label>
                  <div class="location-grid">
                    <select v-model="item.location.zone" required @change="onZoneChange(index, 'from')">
                      <option value="">{{ $t('household.shared.zoneLabel') }}</option>
                      <option v-for="zone in zones" :key="zone.id" :value="zone.id">
                        {{ zone.name }}
                      </option>
                    </select>
                    <select v-model="item.location.row" @change="onRowChange(index, 'from')" :disabled="!item.location.zone">
                      <option value="">{{ $t('household.shared.rowLabel') }}</option>
                      <option v-for="row in item.options.rows" :key="row.id" :value="row.id">
                        {{ row.name }}
                      </option>
                    </select>
                    <select v-model="item.location.rack" @change="onRackChange(index, 'from')" :disabled="!item.location.row">
                      <option value="">{{ $t('household.shared.rackLabel') }}</option>
                      <option v-for="rack in item.options.racks" :key="rack.id" :value="rack.id">
                        {{ rack.name }}
                      </option>
                    </select>
                    <select v-model="item.location.level" :disabled="!item.location.rack">
                      <option value="">{{ $t('household.shared.levelLabel') }}</option>
                      <option v-for="level in item.options.levels" :key="level.id" :value="level.id">
                        {{ level.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Адрес назначения для перемещения -->
                <div v-if="type === 'movement'" class="location-group">
                  <label class="group-label">{{ $t('household.operationForm.toLabel') }}</label>
                  <div class="location-grid">
                    <select v-model="item.locationTo.zone" required @change="onZoneChange(index, 'to')">
                      <option value="">{{ $t('household.shared.zoneLabel') }}</option>
                      <option v-for="zone in zones" :key="zone.id" :value="zone.id">
                        {{ zone.name }}
                      </option>
                    </select>
                    <select v-model="item.locationTo.row" @change="onRowChange(index, 'to')" :disabled="!item.locationTo.zone">
                      <option value="">{{ $t('household.shared.rowLabel') }}</option>
                      <option v-for="row in item.optionsTo.rows" :key="row.id" :value="row.id">
                        {{ row.name }}
                      </option>
                    </select>
                    <select v-model="item.locationTo.rack" @change="onRackChange(index, 'to')" :disabled="!item.locationTo.row">
                      <option value="">{{ $t('household.shared.rackLabel') }}</option>
                      <option v-for="rack in item.optionsTo.racks" :key="rack.id" :value="rack.id">
                        {{ rack.name }}
                      </option>
                    </select>
                    <select v-model="item.locationTo.level" :disabled="!item.locationTo.rack">
                      <option value="">{{ $t('household.shared.levelLabel') }}</option>
                      <option v-for="level in item.optionsTo.levels" :key="level.id" :value="level.id">
                        {{ level.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <button
                  v-if="items.length > 1"
                  type="button"
                  @click="handleRemoveItem(index)"
                  class="remove-item-btn"
                >
                  <LucideIcon :size="14" class="mr-1">
                    <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </LucideIcon>
                  {{ $t('household.operationForm.removePositionBtn') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Примечания -->
          <div class="form-group">
            <label>{{ $t('household.operationForm.notesLabel') }}</label>
            <textarea v-model="formData.notes" rows="3" :placeholder="$t('household.operationForm.notesPlaceholder')"></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button type="submit" class="btn-primary large flex-1">
            <LucideIcon :size="18" class="mr-2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v13a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
            </LucideIcon>
            {{ $t('household.operationForm.submitBtn') }}
          </button>
          <button type="button" @click="$emit('close')" class="btn-secondary large flex-1">{{ $t('common.cancel') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue';
import DatePicker from '@/components/widgets/DatePicker.vue';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { 
  getWarehouses, 
  getWarehouseZones, 
  getRows, 
  getRacks, 
  getLevels 
} from '@/api/household_warehouses';
import { getCounterparties } from '@/api/household_counterparties';
import { createOperation } from '@/api/household_operations';

export default {
  name: 'OperationForm',
  components: { LucideIcon, DatePicker },
  props: {
    type: { type: String, required: true }
  },
  data() {
    return {
      nomenclatures: [],
      warehouses: [],
      counterparties: [],
      formData: {
        date: new Date().toISOString().split('T')[0],
        warehouseId: '',
        counterpartyId: '',
        notes: '',
      },
      zones: [],
      items: [this.createEmptyItem()],
    };
  },
  computed: {
    filteredCounterparties() {
      if (this.type === 'receipt') {
        const data = this.counterparties.map(c => ({ ...c, type: c.counterparty_type || c.type }));
        return data.filter(c => ['dealer', 'dealer_seller', 'supplier', 'both'].includes(c.type));
      }
      if (this.type === 'consumption') {
        const data = this.counterparties.map(c => ({ ...c, type: c.counterparty_type || c.type }));
        return data.filter(c => ['seller', 'dealer_seller', 'customer', 'both'].includes(c.type));
      }
      return [];
    }
  },
  watch: {
    'formData.warehouseId': {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          try {
            const res = await getWarehouseZones({ warehouse: newVal });
            const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
            this.zones = this.sortItems(data);
            
            // Reset item zones when warehouse changes
            this.items.forEach(it => {
              it.location.zone = '';
              it.options.rows = [];
              if (it.locationTo) {
                it.locationTo.zone = '';
                it.optionsTo.rows = [];
              }
            });
          } catch (e) {
            console.error('Failed to load zones for warehouse:', newVal, e);
          }
        }
      }
    }
  },
  async created() {
    const [nomRes, whRes, cpRes] = await Promise.all([
      getNomenclatures(),
      getWarehouses(),
      getCounterparties()
    ]);
    this.nomenclatures = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
    this.warehouses = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.results || []);
    this.counterparties = Array.isArray(cpRes.data) ? cpRes.data : (cpRes.data?.results || []);
    
    if (this.warehouses.length > 0) {
      if (!this.formData.warehouseId) this.formData.warehouseId = this.warehouses[0].id;
    }
    if (this.nomenclatures.length > 0) {
      if (this.items.length > 0) this.items[0].nomenclatureId = this.nomenclatures[0].id;
    }
  },
  methods: {
    sortItems(items) {
      return [...items].sort((a, b) => {
        const aNum = parseInt(a.name?.match(/\d+/) || 0);
        const bNum = parseInt(b.name?.match(/\d+/) || 0);
        if (aNum && bNum) return aNum - bNum;
        return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' });
      });
    },
    createEmptyItem() {
      return {
        nomenclatureId: '',
        quantity: 0,
        cost: 0,
        location: { zone: '', row: '', rack: '', level: '' },
        options: { rows: [], racks: [], levels: [] },
        locationTo: this.type === 'movement' ? { zone: '', row: '', rack: '', level: '' } : undefined,
        optionsTo: this.type === 'movement' ? { rows: [], racks: [], levels: [] } : undefined,
        batchNumber: '',
      };
    },
    handleAddItem() {
      const newItem = this.createEmptyItem();
      if (this.nomenclatures.length > 0) newItem.nomenclatureId = this.nomenclatures[0].id;
      this.items.push(newItem);
    },
    handleRemoveItem(index) {
      this.items.splice(index, 1);
    },
    onNomenclatureChange(index) {
      const item = this.items[index];
      const nom = this.nomenclatures.find(n => n.id === item.nomenclatureId);
      if (nom) {
        item.cost = nom.price * item.quantity;
      }
    },
    onQuantityChange(index) {
      this.onNomenclatureChange(index);
    },
    async onZoneChange(index, side = 'from') {
      const item = this.items[index];
      const loc = side === 'from' ? item.location : item.locationTo;
      const opts = side === 'from' ? item.options : item.optionsTo;
      
      loc.row = '';
      loc.rack = '';
      loc.level = '';
      opts.racks = [];
      opts.levels = [];
      
      if (loc.zone) {
        try {
          const res = await getRows({ zone: loc.zone });
          const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
          opts.rows = this.sortItems(data);
        } catch (e) {
          console.error('Failed to load rows for zone:', loc.zone, e);
          opts.rows = [];
        }
      } else {
        opts.rows = [];
      }
    },
    async onRowChange(index, side = 'from') {
      const item = this.items[index];
      const loc = side === 'from' ? item.location : item.locationTo;
      const opts = side === 'from' ? item.options : item.optionsTo;
      
      loc.rack = '';
      loc.level = '';
      opts.levels = [];
      
      if (loc.row) {
        try {
          const res = await getRacks({ row: loc.row });
          const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
          opts.racks = this.sortItems(data);
        } catch (e) {
          console.error('Failed to load racks for row:', loc.row, e);
          opts.racks = [];
        }
      } else {
        opts.racks = [];
      }
    },
    async onRackChange(index, side = 'from') {
      const item = this.items[index];
      const loc = side === 'from' ? item.location : item.locationTo;
      const opts = side === 'from' ? item.options : item.optionsTo;
      
      loc.level = '';
      
      if (loc.rack) {
        try {
          const res = await getLevels({ rack: loc.rack });
          const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
          opts.levels = this.sortItems(data);
        } catch (e) {
          console.error('Failed to load levels for rack:', loc.rack, e);
          opts.levels = [];
        }
      } else {
        opts.levels = [];
      }
    },
    async handleSubmit() {
      const typeMap = {
        receipt: 'add',
        consumption: 'expense',
        movement: 'move',
        writeoff: 'cancellation',
        inventory: 'inventory'
      };

      try {
        const payload = {
          date: this.formData.date,
          warehouse: Number(this.formData.warehouseId),
          counterparty: this.formData.counterpartyId ? Number(this.formData.counterpartyId) : null,
          operation_type: typeMap[this.type],
          description: this.formData.notes || '',
          zone_id: this.items[0]?.location?.zone ? Number(this.items[0].location.zone) : null,
          nomenclatures: this.items.map(it => ({
            nomenclature: Number(it.nomenclatureId),
            quantity: Number(it.quantity),
            cost: Number(it.cost),
            batch: String(it.batchNumber || ''),
            zone_id: it.location.zone ? Number(it.location.zone) : null,
            level: it.location.level ? Number(it.location.level) : null,
            second_level: it.locationTo?.level ? Number(it.locationTo.level) : null
          }))
        };

        if (!payload.warehouse) throw new Error(this.$t('household.operationForm.errorSelectWarehouse'));
        if (payload.nomenclatures.some(n => !n.nomenclature)) throw new Error(this.$t('household.operationForm.errorSelectNomenclature'));

        await createOperation(payload);
        this.$emit('saved');
      } catch (e) {
        console.error('Operation creation failed:', e);
        const errorMsg = e.payload?.detail || e.message || this.$t('household.operationForm.errorServer');
        alert(`${this.$t('household.operationForm.errorSavePrefix')}: ${errorMsg}`);
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.modal-content.large {
  background: #ffffff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 950px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f1f1f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal-title { color: #030229; font-size: 1.5rem; font-weight: 700; margin: 0; }
.close-btn { background: #f8fafc; border: none; color: #94a3b8; cursor: pointer; padding: 0.5rem; border-radius: 50%; transition: all 0.2s; }
.close-btn:hover { background: #f1f5f9; color: #ef4444; }

.modal-form-scroll { overflow-y: auto; flex: 1; display: flex; flex-direction: column; }
.form-body { padding: 2rem; flex: 1; }

.form-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; margin-bottom: 2.5rem; }
.form-group { margin-bottom: 1.25rem; }
.form-group label { display: block; color: #4b5563; font-size: 0.875rem; font-weight: 600; margin-bottom: 0.5rem; }
.form-group input, .form-group select, .form-group textarea, .location-grid input, .location-grid select {
  width: 100%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  color: #111827;
  outline: none;
  box-sizing: border-box;
  min-width: 0;
  transition: all 0.2s;
}
.form-group input:focus, .form-group select:focus { border-color: #3b82f6; background: white; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1); }

.items-section { margin-bottom: 2.5rem; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem; }
.section-title { color: #030229; font-size: 1.125rem; font-weight: 700; margin: 0; }

.items-list { display: flex; flex-direction: column; gap: 1.25rem; }
.item-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 1rem; padding: 1.5rem; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }

.item-grid-top { 
  display: grid; 
  grid-template-columns: 3fr 1fr 1fr 1.5fr; 
  gap: 1rem; 
  margin-bottom: 1.5rem; 
}

.location-group { margin-bottom: 1rem; }
.group-label { display: block; color: #717182; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem; }
.location-grid { 
  display: grid; 
  grid-template-columns: 1.5fr 1fr 1fr 1fr; 
  gap: 0.75rem; 
}

.remove-item-btn {
  background: transparent;
  border: none;
  color: #ef4444;
  font-size: 0.825rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  margin-top: 0.5rem;
  transition: opacity 0.2s;
}
.remove-item-btn:hover { opacity: 0.8; }

.modal-footer {
  padding: 1.5rem 2rem;
  background: #f8fafc;
  border-top: 1px solid #f1f1f4;
  display: flex;
  gap: 1rem;
}

.btn-primary.small { padding: 0.5rem 1rem; font-size: 0.825rem; }
.btn-primary { background: #2563eb; color: #fff; border: none; border-radius: 0.75rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.btn-primary:hover { background: #1d4ed8; }

.btn-secondary { background: #ffffff; color: #4b5563; border: 1px solid #e5e7eb; border-radius: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.btn-secondary:hover { background: #f9fafb; border-color: #d1d5db; }

.btn-primary.large, .btn-secondary.large { padding: 1rem 2rem; font-size: 1rem; }

.flex-1 { flex: 1; }
.mr-1 { margin-right: 0.25rem; }
.mr-2 { margin-right: 0.5rem; }
</style>
